// ============================================================
// pages/MyBookingsPage.jsx — Customer Booking History (Firebase)
// ============================================================

import React from "react";
import { useBookings } from "../hooks/useFirestore";
import { BookingService } from "../services/firebaseServices";

export default function MyBookingsPage({ showToast }) {
  const { bookings, loading, refresh } = useBookings(false);

  const handleCancel = async (booking) => {
    if (!window.confirm("Cancel this booking? This cannot be undone.")) return;
    try {
      await BookingService.cancelMyBooking(booking.id, "Cancelled by customer");
      showToast("Booking cancelled", "success");
      refresh();
    } catch (err) {
      showToast(err.message || "Failed to cancel", "error");
    }
  };

  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>My Bookings</h2>
          <p>Track your current and past orders</p>
        </div>
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading your bookings...</p></div>
      ) : bookings.length === 0 ? (
        <div className="empty-state">
          <h3>No bookings yet</h3>
          <p>Place your first order and it will appear here!</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {bookings.map((b) => (
            <div key={b.id} className="card" style={{ padding: "1.25rem 1.5rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
                    <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: "0.95rem" }}>
                      {b.bookingNumber || `#${b.id.slice(0, 8)}`}
                    </span>
                    <span className={`status-badge status-${b.status?.replace("_", "-")}`}>
                      {b.status?.replace("_", " ")}
                    </span>
                  </div>

                  {/* Items */}
                  <div style={{ marginBottom: "0.5rem" }}>
                    {b.items?.map((item, i) => (
                      <div key={i} style={{ fontSize: "0.9rem" }}>
                        <strong>{item.productName}</strong>
                        {item.selectedSize && <span style={{ color: "var(--cocoa)" }}> ({item.selectedSize})</span>}
                        {item.quantity > 1 && <span style={{ color: "var(--cocoa)" }}> × {item.quantity}</span>}
                        <span style={{ color: "var(--cocoa)" }}> — Rs {item.subtotal}</span>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: "flex", gap: "1.5rem", fontSize: "0.82rem", color: "var(--cocoa)" }}>
                    <span>📅 {formatDate(b.pickupDate)}</span>
                    <span>⏰ {b.pickupTime || "—"}</span>
                    <span style={{ textTransform: "capitalize" }}>📦 {b.deliveryMethod || "pickup"}</span>
                  </div>

                  {b.items?.[0]?.specialNotes && (
                    <div style={{ fontSize: "0.82rem", color: "var(--cocoa)", marginTop: "0.25rem", fontStyle: "italic" }}>
                      Note: {b.items[0].specialNotes}
                    </div>
                  )}
                </div>

                <div style={{ textAlign: "right" }}>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.3rem", fontWeight: 700 }}>
                    Rs {b.total || 0}
                  </div>
                  {b.discount > 0 && (
                    <div style={{ fontSize: "0.78rem", color: "var(--sage-dark)" }}>
                      Saved Rs {b.discount}
                    </div>
                  )}
                  {b.status === "pending" && (
                    <button className="btn btn-danger btn-sm" style={{ marginTop: "0.75rem" }}
                      onClick={() => handleCancel(b)}>
                      Cancel Booking
                    </button>
                  )}
                </div>
              </div>

              {/* Status Timeline */}
              {b.statusHistory?.length > 0 && (
                <div style={{ marginTop: "1rem", paddingTop: "0.75rem", borderTop: "1px solid rgba(0,0,0,0.05)" }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--cocoa)", marginBottom: "0.5rem" }}>
                    Status History
                  </div>
                  <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                    {b.statusHistory.map((sh, i) => (
                      <div key={i} style={{ fontSize: "0.78rem", color: "var(--cocoa)" }}>
                        <span className={`status-badge status-${sh.status?.replace("_", "-")}`} style={{ fontSize: "0.7rem" }}>
                          {sh.status?.replace("_", " ")}
                        </span>
                        {sh.changedAt && (
                          <span style={{ marginLeft: 4 }}>
                            {formatDate(sh.changedAt)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
